import React from 'react';
import { useNavigate } from 'react-router';
import { Activity, Settings, Globe, Clock, Edit, ExternalLink, Trash2, Shield, AlertTriangle, Database } from 'lucide-react';
import { Monitor, MonitorStatus, SSLCertificateInfo } from '../types';
import { formatResponseTime, formatUptime, getStatusColor, getStatusIcon } from '../utils/api';

interface MonitorCardProps {
  monitor: Monitor;
  status?: MonitorStatus;
  onEdit?: (monitor: Monitor) => void;
  onDelete?: (id: number) => void;
}

const MonitorCard: React.FC<MonitorCardProps> = ({
  monitor,
  status,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const isUp = status?.is_currently_up ?? false;
  const uptimePercentage = status?.uptime_percentage ?? 0;
  const averageResponseTime = status?.average_response_time ?? 0;
  const lastPing = status?.last_ping;
  const totalPings = status?.total_pings ?? 0;

  // Parse SSL certificate info if available
  const sslInfo: SSLCertificateInfo | null = monitor.type === 'ssl' && lastPing?.ssl_info 
    ? (() => {
        try {
          return JSON.parse(lastPing.ssl_info);
        } catch {
          return null;
        }
      })()
    : null;

  const handleEdit = () => {
    onEdit?.(monitor);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${monitor.name}"?`)) {
      onDelete?.(monitor.id);
    }
  };

  const handleViewDetails = () => {
    navigate(`/monitor/${monitor.id}`);
  };

  const handleVisitUrl = () => {
    window.open(monitor.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow duration-300">
      <div className="card-body">
        {/* Header with status indicator */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`badge badge-lg ${getStatusColor(isUp)}`}>
              {getStatusIcon(isUp)} {isUp ? 'UP' : 'DOWN'}
            </div>
            <div className="flex items-center gap-1 text-sm text-base-content/70">
              <Activity className="w-4 h-4" />
              {monitor.type}
            </div>
            {monitor.alerts_enabled !== false && (
              <div className="flex items-center gap-1 text-sm text-success">
                <AlertTriangle className="w-4 h-4" />
                Alerts
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button" className="btn btn-ghost btn-sm">
              <Settings className="w-4 h-4" />
            </div>
            <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
              <li>
                <button onClick={handleEdit} className="flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit Monitor
                </button>
              </li>
              <li>
                <button onClick={handleViewDetails} className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  View Details
                </button>
              </li>
              <li>
                <button onClick={handleVisitUrl} className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Visit URL
                </button>
              </li>
              <li>
                <button 
                  onClick={handleDelete} 
                  className="flex items-center gap-2 text-error"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Monitor
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Monitor name and URL */}
        <div className="mb-4">
          <h3 
            className="card-title text-lg mb-2 cursor-pointer hover:text-primary transition-colors"
            onClick={() => navigate(`/monitor/${monitor.id}`)}
          >
            {monitor.name}
          </h3>
          <div className="flex items-center gap-2 text-sm text-base-content/70">
            <Globe className="w-4 h-4" />
            <span 
              className="truncate cursor-pointer hover:text-primary transition-colors"
              onClick={() => window.open(monitor.url, '_blank', 'noopener,noreferrer')}
              title="Click to visit website"
            >
              {monitor.url}
            </span>
          </div>
        </div>

        {/* Statistics grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="stat p-0">
            <div className="stat-title text-xs">
              {monitor.type === 'ssl' ? 'Certificate Status' : 'Uptime'}
            </div>
            <div className="stat-value text-lg">
              {monitor.type === 'ssl' && sslInfo 
                ? (sslInfo.is_valid 
                    ? (sslInfo.days_until_expiry <= (monitor.ssl_check_days || 7) 
                        ? `⚠️ ${sslInfo.days_until_expiry}d` 
                        : `✅ ${sslInfo.days_until_expiry}d`)
                    : '❌ Invalid')
                : formatUptime(uptimePercentage)
              }
            </div>
          </div>
          
          <div className="stat p-0">
            <div className="stat-title text-xs">
              {monitor.type === 'ssl' ? 'Check Time' : 'Avg Response'}
            </div>
            <div className="stat-value text-lg">{formatResponseTime(averageResponseTime)}</div>
          </div>
        </div>

        {/* SSL Certificate Details */}
        {monitor.type === 'ssl' && sslInfo && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4" />
              <span className="font-semibold text-sm">SSL Certificate</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Subject:</span>
                <span className="truncate ml-2">{sslInfo.subject}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Issuer:</span>
                <span className="truncate ml-2">{sslInfo.issuer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Expires:</span>
                <span>{new Date(sslInfo.valid_to).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Self-Signed:</span>
                <span>{sslInfo.is_self_signed ? 'Yes' : 'No'}</span>
              </div>
            </div>
            {sslInfo.days_until_expiry <= (monitor.ssl_check_days || 7) && (
              <div className="alert alert-warning mt-2 py-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs">
                  Certificate expires in {sslInfo.days_until_expiry} days
                </span>
              </div>
            )}
          </div>
        )}

        {/* Custom Headers Display */}
        {monitor.headers && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4" />
              <span className="font-semibold text-sm">Custom Headers</span>
            </div>
            <div className="text-xs">
              <pre className="whitespace-pre-wrap break-words text-base-content/80">
                {monitor.headers}
              </pre>
            </div>
          </div>
        )}

        {/* Content Validation Display */}
        {monitor.type === 'functional' && monitor.expected_content && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4" />
              <span className="font-semibold text-sm">Content Validation</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Type:</span>
                <span className="capitalize">{monitor.content_type || 'text'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Expected:</span>
                <span className="truncate ml-2 max-w-[200px]">{monitor.expected_content}</span>
              </div>
            </div>
          </div>
        )}

        {/* Protocol-Specific Display */}
        {(monitor.type === 'tcp' || monitor.type === 'udp') && monitor.port && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-4 h-4" />
              <span className="font-semibold text-sm">{monitor.type.toUpperCase()} Configuration</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Port:</span>
                <span>{monitor.port}</span>
              </div>
            </div>
          </div>
        )}

        {monitor.type === 'dns' && monitor.host && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4" />
              <span className="font-semibold text-sm">DNS Configuration</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Host:</span>
                <span className="truncate ml-2 max-w-[200px]">{monitor.host}</span>
              </div>
            </div>
          </div>
        )}

        {/* Supabase Keep-Alive Display */}
        {monitor.type === 'supabase' && (
          <div className="bg-base-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-green-500" />
              <span className="font-semibold text-sm">Supabase Keep-Alive</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-base-content/70">Status:</span>
                <span className={isUp ? 'text-success' : 'text-error'}>
                  {isUp ? 'Database Active' : 'Database Unreachable'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-base-content/70">Interval:</span>
                <span>{Math.round(monitor.interval / 3600000)}h (prevents 7-day pause)</span>
              </div>
            </div>
          </div>
        )}

        {/* Last ping information */}
        {lastPing && (
          <div className="flex items-center justify-between text-sm text-base-content/70 mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Last ping: {new Date(lastPing.created_at).toLocaleTimeString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>Status: {lastPing.status_code}</span>
              <span>•</span>
              <span>{formatResponseTime(lastPing.response_time)}</span>
            </div>
          </div>
        )}

        {/* Monitor configuration */}
        <div className="flex items-center justify-between text-xs text-base-content/60">
          <div className="flex items-center gap-4">
            <span>Interval: {Math.round(monitor.interval / 1000)}s</span>
            {monitor.type !== 'ssl' && monitor.type !== 'supabase' && (
              <span>Expected: {monitor.expected_status || 200}</span>
            )}
            {monitor.type === 'ssl' && (
              <span>Alert: {monitor.ssl_check_days || 7}d before</span>
            )}
            <span>Timeout: {Math.round((monitor.timeout || 10000) / 1000)}s</span>
          </div>
          <span>Total pings: {totalPings}</span>
        </div>

        {/* Error message if last ping failed */}
        {lastPing && !lastPing.is_up && (lastPing.error_message || lastPing.content_validation_error) && (
          <div className="mt-3 p-2 bg-error/10 border border-error/20 rounded-lg">
            <div className="text-xs font-medium text-error mb-1">Error</div>
            <div className="text-xs text-error/80">
              {lastPing.error_message && <div>{lastPing.error_message}</div>}
              {lastPing.content_validation_error && (
                <div className="mt-1">
                  <strong>Content Validation:</strong> {lastPing.content_validation_error}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorCard; 